import React, { useEffect, useState } from "react"
import { useFixedFees } from "../hooks"
import { useSites } from "../../../hooks/useSites"
import { useBrands } from "../../../hooks/useBrands"
import { useSiteBrands } from "../../../hooks/useSiteBrands"
import { ModuleVOConfigScope } from "../moduleVoNavigation"
import { scopeBadgeClass } from "../utils/fixedFeeUtils"
import UnsavedChangesAlert from "./UnsavedChangesAlert"
import FixedFeeScopeSelectors from "./FixedFeeScopeSelectors"

declare const window: any

interface Props {
    mode: 'edit' | 'config'
    scope: ModuleVOConfigScope
    brand?: string | null
    dealershipIds?: number[]
}

/**
 * Un seul tableau, quel que soit le contexte (global, site, marque, ou site+marque) : colonnes
 * Actif / Périmètre / Type de frais / Montant / Action. Une ligne verrouillée (miroir d'une
 * racine ailleurs — cf. useFixedFees) n'expose que la case Actif ; une ligne libre (racine à ce
 * scope) est entièrement éditable et supprimable. Contrairement à VariableFees/MarginValuation,
 * les frais fixes ne gèrent pas le multi-site ni la multi-marque : uniquement un site seul, une
 * marque seule, ou une combinaison des deux (selects simples, via FixedFeeScopeSelectors).
 */
export default function FixedFees({ mode, scope, brand = null, dealershipIds = [] }: Props) {
    // En édition, la cible vient des paramètres d'URL : on sait déjà si elle comporte une marque
    // ou non, pas besoin de deviner ni de tout charger par défaut. En configuration, seul le scope
    // 'brand' utilise réellement allBrandsData (select principal + filtre des sites) : le scope
    // 'site' obtient ses marques via siteBrands (useSiteBrands, plus léger, un seul site), et le
    // scope 'global' n'affiche aucun select — donc pas besoin d'attendre allBrandsData pour eux.
    const needsBrandData = mode === 'edit' ? brand !== null : scope === 'brand'
    const hasSecondaryTarget = mode === 'edit' ? (scope === 'site' ? brand !== null : dealershipIds.length > 0) : true

    const sites = useSites()
    const allBrandsData = useBrands(sites, needsBrandData)

    // useSites()/useBrands() résolvent à des vitesses différentes : sans cette porte, le
    // préremplissage ci-dessous poserait selectedSite dès que les sites arrivent, puis
    // selectedBrand seulement une fois les marques chargées (plus lent) — deux fetch au lieu d'un,
    // le premier avec un site seul erroné le temps que la marque arrive.
    const sitesPending = mode === 'edit' && dealershipIds.length > 0 && sites.length === 0
    const brandPending = mode === 'edit' && brand !== null && allBrandsData.loading

    const {
        rows,
        selectedSite,
        setSelectedSite,
        selectedBrand,
        setSelectedBrand,
        hasChanges,
        submitting,
        loading,
        isSiteMode,
        isBrandMode,
        isLocked,
        scopeLabel,
        updateField,
        addRow,
        deleteRow,
        save,
    } = useFixedFees(sitesPending || brandPending)

    const siteBrands = useSiteBrands(selectedSite)

    useEffect(() => {
        window.$('[data-toggle="tooltip"]').tooltip('dispose')
        window.$('[data-toggle="tooltip"]').tooltip()
    })

    // Pré-remplissage en mode édition à partir des paramètres de la ligne d'origine (Overview).
    useEffect(() => {
        if (mode !== 'edit') {
            return
        }

        if (dealershipIds.length > 0) {
            const site = sites.find(s => s.value === dealershipIds[0])
            if (site) {
                setSelectedSite(site)
            }
        }

        if (brand) {
            const brandOption = allBrandsData.brands.find(b => b.value === brand)
            setSelectedBrand(brandOption ?? null)
        }
    }, [ mode, brand, dealershipIds, sites, allBrandsData ])

    // En config, sans marque/site choisi, il n'y a pas encore de cible : ce qui serait affiché
    // est la config globale, qu'on ne veut ni montrer ni pouvoir modifier depuis cette page.
    const hasSelection = scope === 'global' || selectedSite !== null || selectedBrand !== null
    const wantsTable = mode === 'edit' || hasSelection

    // On n'affiche le tableau qu'une fois tout ce dont on a besoin chargé (sites, marques si
    // nécessaires, lignes de frais fixes) : sinon il flashe avec des données partielles/périmées
    // le temps que chaque requête réponde.
    const dataReady = sites.length > 0 && (!needsBrandData || !allBrandsData.loading) && !loading
    const showTable = wantsTable && dataReady

    // Le tri par ordre alphabétique n'est recalculé qu'au chargement des données depuis l'API
    // (premier rendu, changement de site/marque, ou après enregistrement — tous font passer
    // `loading` par true puis false) : sinon, taper dans un champ "Type de frais" changerait le
    // tri à chaque frappe et ferait sauter les lignes les unes par rapport aux autres.
    const [ orderedRowIds, setOrderedRowIds ] = useState<number[]>([])

    useEffect(() => {
        if (loading) {
            return
        }
        setOrderedRowIds([ ...rows ].sort((a, b) => a.fee_type.localeCompare(b.fee_type, 'fr')).map(row => row.rowId))
    }, [ loading ])

    const rowById = new Map(rows.map(row => [ row.rowId, row ]))
    const knownRowIds = new Set(orderedRowIds)
    const sortedRows = [
        ...orderedRowIds.map(rowId => rowById.get(rowId)).filter((row): row is typeof rows[number] => row !== undefined),
        // Lignes ajoutées depuis le dernier tri (ex : "Ajouter une ligne") : affichées à la suite,
        // sans perturber l'ordre déjà établi.
        ...rows.filter(row => !knownRowIds.has(row.rowId)),
    ]

    return (
        <div className="container-fluid mb-2">
            <div className="row">
                <div className="col">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted">Configurez les frais fixes</span>
                        {showTable && (hasChanges || mode === 'config') && <UnsavedChangesAlert hasChanges={hasChanges} submitting={submitting} onSubmit={save} />}
                    </div>

                    <FixedFeeScopeSelectors
                        scope={scope}
                        disabled={mode === 'edit'}
                        sites={sites}
                        allBrandsData={allBrandsData}
                        siteBrands={siteBrands}
                        selectedSite={selectedSite}
                        onSiteChange={setSelectedSite}
                        selectedBrand={selectedBrand}
                        onBrandChange={setSelectedBrand}
                        hasSecondaryTarget={hasSecondaryTarget}
                    />

                    {!wantsTable && (
                        <p className="text-muted mb-2">
                            <small>
                                <i className="fa fa-info-circle mr-1"></i>
                                Sélectionnez un site et/ou une marque pour configurer les frais fixes.
                            </small>
                        </p>
                    )}

                    {wantsTable && !dataReady && (
                        <p className="text-muted mb-2">
                            <small>
                                <i className="fa fa-spinner fa-spin mr-1"></i>
                                Chargement...
                            </small>
                        </p>
                    )}

                    {showTable && (
                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    <th>Actif</th>
                                    <th>Périmètre</th>
                                    <th>Type de frais</th>
                                    <th>Montant</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map(row => {
                                    const locked = isLocked(row)
                                    return (
                                        <tr key={row.rowId}>
                                            <td className="text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={row.is_active}
                                                    onChange={(e) => updateField(row.rowId, 'is_active', e.target.checked)}
                                                />
                                                {row.hasConflict && (
                                                    <i
                                                        className="fa fa-info-circle text-warning ml-1"
                                                        data-toggle="tooltip"
                                                        data-trigger="hover"
                                                        data-placement="top"
                                                        data-html="true"
                                                        title="Valeurs différentes entre le site et la marque — montant le plus élevé et actif retenus par défaut."
                                                    ></i>
                                                )}
                                            </td>
                                            <td>
                                                <span className={scopeBadgeClass(scopeLabel(row))}>{scopeLabel(row)}</span>
                                            </td>
                                            <td>
                                                {locked ? row.fee_type : (
                                                    <input
                                                        className="form-control"
                                                        value={row.fee_type}
                                                        onChange={(e) => updateField(row.rowId, 'fee_type', e.target.value)}
                                                    />
                                                )}
                                            </td>
                                            <td>
                                                {locked ? `${row.amount}` : (
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={row.amount}
                                                        onChange={(e) => updateField(row.rowId, 'amount', Number(e.target.value))}
                                                    />
                                                )}
                                            </td>
                                            <td>
                                                {!locked && (
                                                    <button className="btn btn-danger btn-sm" onClick={() => deleteRow(row.rowId)}>
                                                        Supprimer
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}
                                <tr>
                                    <td colSpan={5}>
                                        <button className="btn btn-outline-secondary btn-sm" onClick={addRow}>
                                            Ajouter une ligne
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    )
}
